import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { useState, useEffect, useCallback, useRef } from 'react'
import { Edit, Trash2, HardDriveDownload, HardDriveUpload, AlertTriangle, Infinity as InfinityIcon } from 'lucide-react'

import CommonModal from '@/components/common/modal/CommonModal'
import CardOne from '@/components/common/card/CardOne'
import NormalTopBar from '@/components/common/bar/NormalTopBar'
import ToastMessage from '@/components/common/toast/ToastMessage'
import { useMissionCalendarQuery } from '@/hooks/queries/mission'
import { getMissionCalendarApi } from '@/api/missionApi'
import { useStoreMissionMutation, useDeleteMissionMutation } from '@/hooks/queries/mission/mutation'

// 타입 정의
interface DayData {
  day: number
  status: 'completed' | 'planned' | 'not_yet'
}

interface MonthData {
  month: string
  days: DayData[]
  monthIndex: number
  year: number
}

const MissionDetailPage = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [calendarData, setCalendarData] = useState<MonthData[]>([])
  const [isStored, setIsStored] = useState(false)
  const [toast, setToast] = useState({ isVisible: false, message: '' })
  const queryClient = useQueryClient()
  // 현재 로딩 중인 달들을 추적하는 ref
  const loadingMonths = useRef<Set<string>>(new Set())
  // calendarData의 현재 상태를 추적하는 ref
  const calendarDataRef = useRef<MonthData[]>([])
  // 스크롤 컨테이너 ref (중앙 위치로 스크롤하기 위함)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // 이번주 월요일과 일요일 날짜 계산 (HomePage와 동일한 로직)
  const getWeekDates = () => {
    const today = new Date()
    const currentDay = today.getDay() // 0: 일요일, 1: 월요일, ..., 6: 토요일
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay // 월요일까지의 거리
    const monday = new Date(today.getTime() + mondayOffset * 24 * 60 * 60 * 1000)
    const sunday = new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000)

    return {
      start_date: monday.toISOString().split('T')[0],
      end_date: sunday.toISOString().split('T')[0]
    }
  }

  // calendarData가 변경될 때마다 ref를 업데이트
  useEffect(() => {
    calendarDataRef.current = calendarData
  }, [calendarData])

  const { data: missionCalendar } = useMissionCalendarQuery(id ?? '', new Date().toISOString().split('T')[0])

  useEffect(() => {
    setIsStored(missionCalendar?.data.detail.is_stored ?? false)
  }, [missionCalendar])

  const { mutate: storeMission } = useStoreMissionMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['missionDetail', id] })
      queryClient.invalidateQueries({ queryKey: ['missionCalendar', id] })
      queryClient.invalidateQueries({ queryKey: ['missionList'] })
      queryClient.invalidateQueries({ queryKey: ['dailyMission'] })

      // 이번주 미션 현황 업데이트를 위한 date range 쿼리 invalidate
      const weekDates = getWeekDates()
      queryClient.invalidateQueries({ queryKey: ['dailyMissionDateRange', weekDates] })

      const newIsStored = !isStored
      setIsStored(newIsStored)

      // 토스트 메시지 표시
      setToast({
        isVisible: true,
        message: newIsStored ? '미션이 보관되었습니다' : '미션 보관이 \n해제되었습니다'
      })
      setTimeout(() => {
        if (newIsStored) {
          navigate(-1)
        } else {
          navigate('/mission/all', { state: { isStoreMenu: true } }) // '전체 미션' 페이지 중 '보관됨' 탭으로 이동
        }
      }, 400)
    }
  })

  const { mutate: deleteMission } = useDeleteMissionMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['missionDetail', id] })
      queryClient.invalidateQueries({ queryKey: ['missionCalendar', id, new Date().toISOString().split('T')[0]] })
      queryClient.invalidateQueries({ queryKey: ['missionList'] })
      queryClient.invalidateQueries({ queryKey: ['dailyMission', new Date().toISOString().split('T')[0]] })

      // 이번주 미션 현황 업데이트를 위한 date range 쿼리 invalidate
      const weekDates = getWeekDates()
      queryClient.invalidateQueries({ queryKey: ['dailyMissionDateRange', weekDates] })

      navigate(-1)
    }
  })

  // 월 데이터 생성 함수
  const generateMonth = useCallback(
    async (year: number, month: number): Promise<MonthData> => {
      const monthDate = new Date(year, month, 1)
      const response = await (
        await getMissionCalendarApi(id ?? '', monthDate.toISOString().split('T')[0])
      ).data.calendar
      const monthName = `${monthDate.getFullYear()}년 ${monthDate.getMonth() + 1}월`
      const daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate()
      const days: DayData[] = []

      for (let day = 1; day <= daysInMonth; day++) {
        const dayDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), day)

        // UTC 변환 문제를 방지하기 위해 로컬 시간대로 날짜 문자열 생성
        const year = dayDate.getFullYear()
        const month = String(dayDate.getMonth() + 1).padStart(2, '0')
        const dayStr = String(dayDate.getDate()).padStart(2, '0')
        const dateString = `${year}-${month}-${dayStr}`

        const dailyMission = response.find(dm => dm.date === dateString)
        days.push({ day, status: dailyMission?.status ?? 'not_yet' })
      }

      return {
        month: monthName,
        days,
        monthIndex: monthDate.getMonth(),
        year: monthDate.getFullYear()
      }
    },
    [id]
  )

  // 초기 달력 데이터 로드
  useEffect(() => {
    const fetchData = async () => {
      const startDate = new Date()
      const currentYear = startDate.getFullYear()
      const currentMonth = startDate.getMonth()

      const initialMonths: MonthData[] = []

      // 이전 1개월, 현재 월, 다음 1개월을 미리 로드하여 자연스러운 스크롤 경험 제공
      for (let i = -1; i <= 1; i++) {
        const date = new Date(currentYear, currentMonth + i, 1)
        initialMonths.push(await generateMonth(date.getFullYear(), date.getMonth()))
      }

      setCalendarData(initialMonths)
    }
    fetchData()
  }, [generateMonth])

  // 초기 로드 후 현재 달(중앙)로 스크롤 위치 설정
  useEffect(() => {
    if (calendarData.length === 3 && scrollContainerRef.current) {
      // 잠시 대기 후 DOM이 완전히 렌더링된 후 스크롤 (렌더링 완료 보장)
      setTimeout(() => {
        const container = scrollContainerRef.current
        if (container) {
          // 두 번째 달력(현재 달)의 위치로 스크롤
          const currentMonthCalendar = container.children[1] as HTMLElement
          if (currentMonthCalendar) {
            const offsetTop = currentMonthCalendar.offsetTop
            // 현재 달이 잘 보이도록 스크롤 위치 설정
            const scrollPosition = offsetTop - 200 // 위쪽 여백 추가
            container.scrollTo({
              top: Math.max(0, scrollPosition),
              behavior: 'auto' // 초기 설정은 즉시 이동
            })
          }
        }
      }, 50)
    }
  }, [calendarData])

  // 스크롤 이벤트 핸들러
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
      const currentCalendarData = calendarDataRef.current

      // 하단 근처에서 다음 달 로드 (임계값을 더 여유롭게 조정)
      if (scrollHeight - scrollTop - clientHeight < 300) {
        const lastMonth = currentCalendarData[currentCalendarData.length - 1]
        if (lastMonth) {
          const nextDate = new Date(lastMonth.year, lastMonth.monthIndex + 1, 1)
          const nextYear = nextDate.getFullYear()
          const nextMonth = nextDate.getMonth()
          const monthKey = `${nextYear}-${nextMonth}`

          // 이미 해당 달이 존재하거나 로딩 중인지 확인
          const monthExists = currentCalendarData.some(
            month => month.year === nextYear && month.monthIndex === nextMonth
          )

          if (!monthExists && !loadingMonths.current.has(monthKey)) {
            // 로딩 시작 표시
            loadingMonths.current.add(monthKey)

            generateMonth(nextYear, nextMonth)
              .then(newMonth => {
                setCalendarData(prev => {
                  // 한 번 더 중복 체크 (비동기 업데이트 중에 중복이 추가되는 것을 방지)
                  const exists = prev.some(
                    month => month.year === newMonth.year && month.monthIndex === newMonth.monthIndex
                  )
                  if (exists) {
                    return prev
                  }
                  return [...prev, newMonth]
                })
              })
              .catch(error => {
                console.error('Failed to generate month:', error)
              })
              .finally(() => {
                // 로딩 완료 후 제거
                loadingMonths.current.delete(monthKey)
              })
          }
        }
      }

      // 상단 근처에서 이전 달 로드 (임계값을 더 여유롭게 조정)
      if (scrollTop < 200) {
        const firstMonth = currentCalendarData[0]
        if (firstMonth) {
          const prevDate = new Date(firstMonth.year, firstMonth.monthIndex - 1, 1)
          const prevYear = prevDate.getFullYear()
          const prevMonth = prevDate.getMonth()
          const monthKey = `${prevYear}-${prevMonth}`

          // 이미 해당 달이 존재하거나 로딩 중인지 확인
          const monthExists = currentCalendarData.some(
            month => month.year === prevYear && month.monthIndex === prevMonth
          )

          if (!monthExists && !loadingMonths.current.has(monthKey)) {
            // 현재 스크롤 위치 저장
            const currentScrollTop = scrollTop
            const container = scrollContainerRef.current

            // 로딩 시작 표시
            loadingMonths.current.add(monthKey)

            generateMonth(prevYear, prevMonth)
              .then(newMonth => {
                setCalendarData(prev => {
                  // 한 번 더 중복 체크 (비동기 업데이트 중에 중복이 추가되는 것을 방지)
                  const exists = prev.some(
                    month => month.year === newMonth.year && month.monthIndex === newMonth.monthIndex
                  )
                  if (exists) {
                    return prev
                  }
                  return [newMonth, ...prev]
                })

                // 스크롤 위치 보정 (자연스러운 스크롤 경험을 위해)
                setTimeout(() => {
                  if (container) {
                    // 새로 추가된 달력의 높이만큼 스크롤 위치 조정
                    const newFirstCalendar = container.children[0] as HTMLElement
                    if (newFirstCalendar) {
                      const addedHeight = newFirstCalendar.offsetHeight + 12 // gap 포함
                      container.scrollTo({
                        top: currentScrollTop + addedHeight,
                        behavior: 'auto' // 즉시 이동하여 끊김 없는 경험 제공
                      })
                    }
                  }
                }, 10)
              })
              .catch(error => {
                console.error('Failed to generate month:', error)
              })
              .finally(() => {
                // 로딩 완료 후 제거
                loadingMonths.current.delete(monthKey)
              })
          }
        }
      }
    },
    [generateMonth]
  )

  // 날짜 포맷팅 함수
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return `${date.getFullYear()}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getDate().toString().padStart(2, '0')}`
  }

  const weekDays = ['일', '월', '화', '수', '목', '금', '토']

  const handleEdit = () => {
    navigate(`/mission/edit/${id}`)
  }

  const handleDelete = () => {
    setShowDeleteModal(true)
  }

  const confirmDelete = () => {
    deleteMission({ mission_id: id ?? '' })
    setShowDeleteModal(false)
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
  }

  const handleToggleUpload = () => {
    storeMission({ mission_id: Number(id), is_stored: !missionCalendar?.data.detail.is_stored })
  }

  // 특정 날짜가 미션 예정일인지 확인하는 함수
  const isMissionDay = (dayData: DayData): boolean => {
    if (dayData.status === 'planned' || dayData.status === 'completed') {
      return true
    }
    return false
  }

  // 오늘 날짜인지 확인하는 함수
  const isToday = (year: number, month: number, day: number): boolean => {
    const today = new Date()
    const targetDate = new Date(year, month - 1, day)

    return (
      today.getFullYear() === targetDate.getFullYear() &&
      today.getMonth() === targetDate.getMonth() &&
      today.getDate() === targetDate.getDate()
    )
  }

  const renderCalendar = (monthData: (typeof calendarData)[0], index: number) => {
    const year = monthData.year
    const month = monthData.monthIndex + 1
    const firstDayOfMonth = new Date(year, monthData.monthIndex, 1).getDay()
    const emptyDays = Array.from({ length: firstDayOfMonth }, (_, i) => i)

    return (
      <CardOne className='bg-white rounded-2xl px-3 py-5' key={`${monthData.year}-${monthData.monthIndex}-${index}`}>
        <p className='font-medium text-center mb-4 text-sm'>{monthData.month}</p>

        {/* 요일 헤더 */}
        <div className='grid grid-cols-7 gap-1 mb-3'>
          {weekDays.map(day => (
            <div key={day} className='text-center text-sm font-medium py-2'>
              {day}
            </div>
          ))}
        </div>

        {/* 달력 날짜 */}
        <div className='grid grid-cols-7 gap-1'>
          {emptyDays.map(i => (
            <div key={`empty-${i}`} className='h-10'></div>
          ))}
          {monthData.days.map((dayData: DayData) => {
            const isMissionScheduled = isMissionDay(dayData)
            const isCompleted = dayData.status === 'completed'
            const isPlanned = dayData.status === 'planned'
            const isNotYet = dayData.status === 'not_yet'
            const isTodayDate = isToday(year, month, dayData.day)

            // 텍스트 색상 결정
            let textColor = 'text-gray-300' // 기본: 제외일 (매우 연함)
            if (isMissionScheduled) {
              textColor = 'text-white' // 예정일은 모두 흰색 (꽃 배경 때문에)
            }

            return (
              <div key={dayData.day} className='h-10 flex items-center justify-center text-sm relative'>
                <span className={`${textColor} relative z-10 font-medium`}>{dayData.day}</span>
                {isMissionScheduled && (
                  <div className='absolute inset-0 flex items-center justify-center'>
                    {isCompleted && <div className='w-8 h-8 text-2xl'>🌸</div>}
                    {isPlanned && <div className='w-8 h-8 text-2xl grayscale'>🌸</div>}
                    {isNotYet && <></>}
                  </div>
                )}
                {/* 오늘 날짜 표시 점 */}
                {isTodayDate && (
                  <div className='absolute bottom-0 left-1/2 transform -translate-x-1/2'>
                    <div className='w-2 h-2 bg-amber-400 rounded-full'></div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </CardOne>
    )
  }

  // 미션 통계 계산
  const totalDays = missionCalendar?.data.total_days
  const completedDays = missionCalendar?.data.completed_days
  const progressPercentage =
    missionCalendar?.data.total_days === null
      ? `${completedDays}`
      : `${missionCalendar?.data.completed_days}/${totalDays}`

  return (
    <div className='min-h-screen'>
      <NormalTopBar
        title={missionCalendar?.data.detail.name}
        prevButton
        onPrevButtonClick={() => navigate(-1)}
        textClassName='text-xl text-text-header font-semibold'
        rightSide={
          <div className='flex items-center gap-0.5 flex-shrink-0'>
            <button
              onClick={handleEdit}
              className='p-1 text-text-header hover:bg-amber-200/50 rounded-lg transition-colors'
              title='수정'
            >
              <Edit size={18} />
            </button>
            <button
              onClick={handleToggleUpload}
              className='p-1 text-text-header hover:bg-amber-200/50 rounded-lg transition-colors'
              title={isStored ? '다운로드' : '업로드'}
            >
              {isStored ? <HardDriveDownload size={18} /> : <HardDriveUpload size={18} />}
            </button>
            <button
              onClick={handleDelete}
              className='p-1 text-text-header hover:bg-amber-200/50 rounded-lg transition-colors'
              title='삭제'
            >
              <Trash2 size={18} />
            </button>
          </div>
        }
        boxClassName='bg-gradient-to-r from-amber-400 to-yellow-300'
      />
      <div className='p-3 space-y-3 pb-20'>
        {/* 미션 정보 카드들 */}
        <div className='grid grid-cols-3 gap-3'>
          <div className='flex flex-col justify-between bg-white rounded-xl p-3 text-center border border-amber-200'>
            <p className='text-gray-500 text-sm font-medium'>첫걸음</p>
            <p className='text-gray-700 font-bold text-sm'>
              {formatDate(missionCalendar?.data.detail.start_date ?? '')}
            </p>
          </div>
          <div className='flex flex-col justify-between gap-0.5 bg-white rounded-xl p-3 text-center border border-amber-200'>
            <p className='text-gray-500 text-sm font-medium'>
              두근두근
              <br />
              마지막
            </p>
            {missionCalendar?.data.detail.end_date === null ? (
              <div className='flex items-center justify-center'>
                <InfinityIcon size={20} />
              </div>
            ) : (
              <p className='text-gray-700 text-sm font-bold'>
                {formatDate(missionCalendar?.data.detail.end_date ?? '')}
              </p>
            )}
          </div>
          <div className='flex flex-col justify-between bg-white rounded-xl p-3 text-center border border-amber-200'>
            <p className='text-gray-500 text-sm font-medium'>
              얼마나 <br /> 잘했어?
            </p>

            <p className='text-gray-700 text-sm font-bold'>{progressPercentage}</p>
          </div>
        </div>

        {/* 달력들 */}
        <div
          ref={scrollContainerRef}
          className='space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto'
          onScroll={handleScroll}
        >
          {calendarData.map((monthData, index) => renderCalendar(monthData, index))}
        </div>
      </div>

      {/* 토스트 메시지 */}
      <ToastMessage
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={() => setToast({ isVisible: false, message: '' })}
        addClassName='text-center whitespace-pre-line'
      />

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <CommonModal
          hasCloseButton={false}
          onClose={cancelDelete}
          topButtonText='삭제'
          bottomButtonText='취소'
          topButtonOnClick={confirmDelete}
          bottomButtonOnClick={cancelDelete}
          isTopButtonDisabled={false}
          modalContent={
            <div className='text-center mb-6'>
              <AlertTriangle className='w-40 h-40 text-amber-500 mx-auto mb-4' />
              <p className='text-gray-700 text leading-relaxed mb-2'>
                삭제하실 경우 기존 기록이 <br /> 모두 삭제됩니다
              </p>
            </div>
          }
        />
      )}
    </div>
  )
}

export default MissionDetailPage
